import type { InputHTMLAttributes, ReactNode } from "react";
import { forwardRef, useState } from "react";

import clsx from "clsx";

import * as stylex from "@stylexjs/stylex";

import EyeInteractive from "@Assets/EyeInteractive";

import { TextboxAppearanceV2, defaultFalse } from "./TextboxTypes";

import * as portalCss from "../../../src/shared/Portal.module.css";
import * as css from "./TextboxV2.module.css";

const getTextboxAppearanceClassname = (appearance?: TextboxAppearanceV2) => {
    switch (appearance) {
        case TextboxAppearanceV2.PrefixNumber:
            return css.prefixNumber;
        case TextboxAppearanceV2.Standard:
            return css.standard;
        case TextboxAppearanceV2.Round:
        default:
            return `${css.round} ${stylex.props(styles.border).className}`;
    }
};

export interface ITextboxV2 extends InputHTMLAttributes<HTMLInputElement> {
    appearance?: TextboxAppearanceV2;
    clientId?: string;
    className?: string;
    icon?: ReactNode;
    preventPaste?: boolean;
    errorMessage?: string;
}

export const TextboxV2 = forwardRef<HTMLInputElement, ITextboxV2>((props, textBoxRef) => {
    const {
        className,
        icon,
        appearance,
        type,
        clientId,
        preventPaste,
        errorMessage,
        onChange,
        onPaste,
        ...all
    } = props;

    const [typeOverride, setTypeOverride] = useState<React.HTMLInputTypeAttribute>(type);
    const overrideOnChange: React.ChangeEventHandler<HTMLInputElement> =
        type !== "tel"
            ? onChange
            : (event) => {
                  const { value } = event.currentTarget;

                  if (/^[0-9]{0,9}$/.test(value)) {
                      if (onChange) {
                          onChange(event);
                      }
                      return;
                  }

                  event.currentTarget.value = value.substring(0, value.length - 1);

                  event.preventDefault();
                  event.stopPropagation();
              };

    const getIcon = (): ReactNode => {
        if (icon) {
            return icon;
        }

        if (type === "password") {
            const pwVisible = typeOverride !== "password";
            return (
                <EyeInteractive
                    isVisible={pwVisible}
                    color={pwVisible ? "#c01775" : undefined}
                    onClick={() => setTypeOverride(pwVisible ? type : "text")}
                />
            );
        }

        return undefined;
    };

    return (
        <div className={css.textboxV2Component}>
            <div
                className={clsx(
                    css.textboxWrapper,
                    getTextboxAppearanceClassname(appearance),
                    errorMessage && portalCss.hasError,
                    className,
                )}
            >
                <input
                    {...all}
                    ref={textBoxRef}
                    id={clientId || undefined}
                    type={typeOverride}
                    inputMode={typeOverride === "number" ? "numeric" : undefined}
                    onPaste={preventPaste ? defaultFalse : onPaste}
                    onChange={overrideOnChange}
                />
                {getIcon()}
            </div>
            {errorMessage && <p>{errorMessage}</p>}
        </div>
    );
});

const styles = stylex.create({
    border: {
        borderRadius: "1.5625rem",
    },
});
